import { useLayoutEffect, useMemo, useRef } from 'react'
import {
  LARGURA_DA_SEMANA,
  atrasoDoItem,
  diasEntreDatas,
  estadoDaBarra,
  faixaDaGrade,
  geometriaDaBarra,
  periodoDoItem,
  pixels,
  semanasDaGrade,
} from './linha-do-tempo.ts'
import './linha-do-tempo.css'

/**
 * LINHA DO TEMPO da lista de Itens (SPEC-20261002-1241-linha-do-tempo-de-itens,
 * protótipo "Itens de Trabalho - Prototipo.html", alternador "Lista | Linha do
 * tempo").
 *
 * À esquerda, a mesma árvore da lista (código e título, recolher/expandir); à
 * direita, a grade por SEMANA com as barras:
 * - item: barra no período (início → prazo), na cor do status, com o título
 *   dentro; "a fazer" tracejada; em andamento preenche o quanto já foi feito;
 * - item com filhos: barra fina com o progresso da árvore dele;
 * - atraso: extensão listrada do prazo até hoje;
 * - a linha vermelha de HOJE.
 * Clicar numa linha abre o resumo do item, como na lista.
 */

export type LinhaDoTempoLinha =
  | {
      tipo: 'item'
      chave: string
      depth: number
      id: string
      code: string
      title: string
      startAt: string | null
      dueAt: string | null
      status: { id: string; isTerminal: boolean }
      temFilhos: boolean
      recolhido: boolean
      contexto: boolean
      progresso: { feitos: number; total: number }
    }
  | { tipo: 'grupo'; chave: string; depth: number; titulo: string; detalhe: string; recolhido?: boolean }
  | { tipo: 'texto'; chave: string; depth: number; titulo: string; feito: boolean }

const LARGURA_DA_LISTA = 380

export function LinhaDoTempo({
  linhas,
  statusIniciais,
  selecionadoId,
  onSelecionar,
  onAlternar,
}: {
  linhas: LinhaDoTempoLinha[]
  /** Status iniciais do catálogo: a barra deles é "a fazer" (tracejada). */
  statusIniciais: ReadonlySet<string>
  selecionadoId: string | null
  onSelecionar: (id: string) => void
  /** Recolhe/expande pela chave da linha (item ou checklist). */
  onAlternar: (chave: string) => void
}) {
  const hoje = useMemo(() => new Date(), [])
  const faixa = useMemo(
    () => faixaDaGrade(linhas.map((l) => (l.tipo === 'item' ? periodoDoItem(l) : null)), hoje),
    [linhas, hoje],
  )
  const semanas = useMemo(() => semanasDaGrade(faixa, hoje), [faixa, hoje])
  const larguraDaGrade = faixa.semanas * LARGURA_DA_SEMANA
  const xDeHoje = pixels(diasEntreDatas(faixa.inicio, hoje)) + pixels(1) / 2

  // abre com HOJE à vista, duas semanas depois da borda da lista
  const rolagem = useRef<HTMLDivElement | null>(null)
  useLayoutEffect(() => {
    if (rolagem.current) rolagem.current.scrollLeft = Math.max(0, xDeHoje - LARGURA_DA_SEMANA * 2)
    // só na abertura: depois, a rolagem é da pessoa
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <div className="ldt" ref={rolagem} role="table" aria-label="Linha do tempo dos Itens de Trabalho">
      <div className="ldt__miolo" style={{ width: LARGURA_DA_LISTA + larguraDaGrade }}>
        {/* ---------------------------------------------- cabeçalho */}
        <div className="ldt__cab" role="row">
          <div className="ldt__cab-lista" role="columnheader">
            Item
            <span className="ldt__legenda" aria-label="Legenda">
              <span><i className="ldt__amostra ldt__amostra--feito" aria-hidden /> Concluído</span>
              <span><i className="ldt__amostra ldt__amostra--andamento" aria-hidden /> Em andamento</span>
              <span><i className="ldt__amostra ldt__amostra--atraso" aria-hidden /> Atraso</span>
            </span>
          </div>
          <div className="ldt__semanas" role="columnheader">
            {semanas.map((s) => (
              <div key={s.chave} className={`ldt__semana${s.atual ? ' ldt__semana--atual' : ''}`}>
                <strong>
                  {s.dia} <span>{s.mes}</span>
                </strong>
                semana {s.semana}
              </div>
            ))}
          </div>
        </div>

        {/* ---------------------------------------------- linhas */}
        <div className="ldt__corpo">
          <span className="ldt__hoje" style={{ left: LARGURA_DA_LISTA + xDeHoje }} aria-hidden />
          <span className="ldt__hoje-rotulo" style={{ left: LARGURA_DA_LISTA + xDeHoje - 18 }}>
            Hoje
          </span>
          {linhas.map((linha) => {
            if (linha.tipo === 'grupo') {
              return (
                <div key={linha.chave} className="ldt__linha ldt__linha--grupo" role="row">
                  <div className="ldt__nome" style={{ paddingLeft: 20 + linha.depth * 18 }}>
                    {linha.recolhido !== undefined ? (
                      <button
                        type="button"
                        className="ldt__caret"
                        aria-label={linha.recolhido ? `Expandir ${linha.titulo}` : `Recolher ${linha.titulo}`}
                        onClick={() => onAlternar(linha.chave)}
                      >
                        <i className={`fas fa-chevron-${linha.recolhido ? 'right' : 'down'}`} aria-hidden />
                      </button>
                    ) : null}
                    <span className="ldt__grupo">{linha.titulo}</span>
                    {linha.detalhe ? <em className="ldt__detalhe">{linha.detalhe}</em> : null}
                  </div>
                  <div className="ldt__trilho" style={{ width: larguraDaGrade }} />
                </div>
              )
            }
            if (linha.tipo === 'texto') {
              return (
                <div key={linha.chave} className="ldt__linha" role="row">
                  <div className="ldt__nome" style={{ paddingLeft: 20 + linha.depth * 18 }}>
                    <i className={linha.feito ? 'fas fa-check-square' : 'far fa-square'} aria-label={linha.feito ? 'Feita' : 'Em aberto'} />
                    <span className={`ldt__titulo${linha.feito ? ' ldt__titulo--feito' : ''}`}>{linha.titulo}</span>
                  </div>
                  <div className="ldt__trilho" style={{ width: larguraDaGrade }} />
                </div>
              )
            }
            const periodo = periodoDoItem(linha)
            const barra = periodo ? geometriaDaBarra(periodo, faixa.inicio) : null
            const atraso = atrasoDoItem(linha, hoje, faixa.inicio)
            const estado = estadoDaBarra(linha.status, statusIniciais.has(linha.status.id))
            const pct = linha.progresso.total > 0 ? Math.round((linha.progresso.feitos / linha.progresso.total) * 100) : 0
            const selecionado = linha.id === selecionadoId
            const rotuloDoEstado = estado === 'feito' ? 'concluído' : estado === 'andamento' ? 'em andamento' : 'a fazer'
            return (
              <div
                key={linha.chave}
                role="row"
                className={`ldt__linha ldt__linha--item${selecionado ? ' ldt__linha--on' : ''}${linha.contexto ? ' ldt__linha--contexto' : ''}`}
                onClick={() => onSelecionar(linha.id)}
              >
                <div className="ldt__nome" style={{ paddingLeft: 20 + linha.depth * 18 }}>
                  <button
                    type="button"
                    className="ldt__caret"
                    style={{ visibility: linha.temFilhos ? 'visible' : 'hidden' }}
                    aria-label={linha.recolhido ? `Expandir ${linha.title}` : `Recolher ${linha.title}`}
                    aria-expanded={linha.temFilhos ? !linha.recolhido : undefined}
                    onClick={(e) => {
                      e.stopPropagation()
                      onAlternar(linha.id)
                    }}
                  >
                    <i className={`fas fa-chevron-${linha.recolhido ? 'right' : 'down'}`} aria-hidden />
                  </button>
                  <span className="ldt__codigo">{linha.code}</span>
                  <span className={`ldt__titulo${linha.temFilhos ? ' ldt__titulo--pai' : ''}`}>{linha.title}</span>
                </div>
                <div className="ldt__trilho" style={{ width: larguraDaGrade }}>
                  {barra && linha.temFilhos && linha.progresso.total > 0 ? (
                    // pai: barra fina com o progresso da árvore e as pontas marcadas
                    <span
                      className="ldt__fina"
                      style={{ left: barra.esquerda, width: barra.largura }}
                      title={`${linha.code} · ${linha.progresso.feitos}/${linha.progresso.total} concluídos`}
                    >
                      <span className="ldt__fina-feito" style={{ width: `${pct}%` }} />
                    </span>
                  ) : barra ? (
                    <span
                      className={`ldt__barra ldt__barra--${estado}`}
                      style={{ left: barra.esquerda, width: barra.largura }}
                      title={`${linha.code} · ${rotuloDoEstado}`}
                    >
                      {estado === 'andamento' && linha.progresso.total > 0 ? (
                        <span className="ldt__barra-feito" style={{ width: `${pct}%` }} />
                      ) : null}
                      <span className="ldt__barra-texto">{linha.title}</span>
                    </span>
                  ) : null}
                  {atraso ? (
                    <span
                      className="ldt__atraso"
                      style={{ left: atraso.esquerda, width: atraso.largura }}
                      title={`${linha.code} · atrasado`}
                      aria-label="Atrasado"
                    />
                  ) : null}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
